import {
  exportJobSchema,
  type CalendarQuery,
  type ExportJob,
  type FilteredViewExportRequest,
  type SortOrder,
} from '@complan/contracts';
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';

/**
 * The filtered-view export data layer (task 22.6, wired to the task 20.3 API).
 *
 * Screen/export agreement (design.md Property 8, R13.6) is a consequence of the
 * request carrying the *same* filter representation the screen used: the export
 * body's `filters`, `orientation` and `sort` are the current calendar query and
 * sort, the identical values the URL holds and the calendar read ran under. There
 * is no second query to keep in step — the export re-runs the one grammar.
 *
 * An export is a job, not a response (R14.11, R15.9): the POST answers `202` with a
 * job in the `queued`/`running` state, and the client polls `GET /api/exports/:jobId`
 * at least every five seconds (R18.4) until the job reaches a terminal state. On
 * `succeeded` the job carries the download token; `failed` and `timed_out` deliver
 * nothing and say so through `state` alone.
 */

/** Whether an export job has reached a terminal state (no more polling). */
export function isTerminalExportState(state: ExportJob['state']): boolean {
  return state === 'succeeded' || state === 'failed' || state === 'timed_out';
}

/** Builds the calendar-source filtered-view request from the current view state (R13.6). */
export function toFilteredViewRequest(
  query: CalendarQuery,
  sort: SortOrder,
): FilteredViewExportRequest {
  // `orientation` lives on the query; the export request wants it as a peer field
  // alongside the pure filter grammar, so split it out here. `columns` is empty
  // for a calendar export — R15.1 fixes its column set, so the client sends none.
  const { orientation, ...filters } = query;
  return { source: 'calendar', filters, orientation, sort, columns: [] };
}

/** Enqueues a filtered-view export and returns the initial job (R15.1, R18.4). */
export function useRequestFilteredViewExport(): UseMutationResult<
  ExportJob,
  unknown,
  FilteredViewExportRequest
> {
  return useMutation({
    mutationFn: async (request: FilteredViewExportRequest) => {
      const body = await apiFetch<unknown>('/api/exports/filtered-view', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return exportJobSchema.parse(body);
    },
  });
}

/** Fetches one export job's status. */
export async function fetchExportJob(jobId: string): Promise<ExportJob> {
  const body = await apiFetch<unknown>(`/api/exports/${jobId}`);
  return exportJobSchema.parse(body);
}

/**
 * Polls an export job until it terminates (R18.4). `jobId` is `null` before an
 * export has been requested, which disables the query; once set, the query
 * refetches every five seconds while the job is still `queued` or `running`, and
 * stops the moment it reaches a terminal state.
 */
export function useExportJob(jobId: string | null): UseQueryResult<ExportJob, unknown> {
  return useQuery({
    queryKey: queryKeys.exportJob(jobId ?? 'none'),
    queryFn: () => fetchExportJob(jobId as string),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state && isTerminalExportState(state)) {
        return false;
      }
      return EXPORT_POLL_INTERVAL_MS;
    },
  });
}

/** The status-poll cadence: five seconds, the interval R18.4 requires the client to poll within. */
export const EXPORT_POLL_INTERVAL_MS = 5000;
